<template>
  <v-dialog
    :value="value" @input="$emit('input')"
    width="700"
  >
    <v-card>
      <v-card-title class="text-h5">
        <template v-if="item.entity">Update Settings {{ item.entity }}</template>
        <template v-else>Create Settings</template>
      </v-card-title>

      <v-card-text>
        <template v-for="field in editableFields">
          <template v-if="field.type === 'group'">

            <fieldset class="rounded-8 px-4 mt-4 pb-4">
              <legend class="px-2">{{ field.label }}</legend>
              <template v-if="field.key === 'loyalty'">
                <loyalty-settings
                  :item="item"
                  :loading="loading"
                  :fields="field.fields"
                  :game-types="gameTypes"
                  :entity-name="item.entity"
                  @update-item="$emit('update-item', $event)"
                  @validation-change="handleLoyaltyValidationChange"
                />
              </template>

              <template v-else-if="field.key === 'mail_settings'">
                <field-renderer
                  v-for="(subField, subIndex) in field.fields"
                  :key="`${field.key}-field-${subIndex}`"
                  :field="getMailFieldWithChoices(subField)"
                  :item="item"
                  :loading="loading"
                />
              </template>

              <template v-else>
                <field-renderer
                  v-for="(subField, subIndex) in field.fields"
                  :key="`${field.key}-field-${subIndex}`"
                  :field="subField"
                  :item="item"
                  :loading="loading"
                />
              </template>

              <v-divider
                :key="`group-divider-${field.key}`"
              />
            </fieldset>
          </template>


          <field-renderer
            v-else
            :key="`field-${field.key}`"
            :field="field"
            :item="item"
            :loading="loading"
            :hold-time-list="holdTimeList"
          />
        </template>
      </v-card-text>
      <v-divider></v-divider>

      <v-card-actions>
        <v-btn
          color="warning"
          text
          :loading="loading"
          :disabled="loading"
          @click.native="$emit('input')"
        >
          Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          text
          :loading="loading"
          :disabled="loading || !isLoyaltyValid"
          @click="save(item)"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { removeDuplicateValues } from '@/utils/helper'
  import { API } from '@/utils/api'
  import FieldRenderer from './FieldRenderer.vue'
  import LoyaltySettings from './LoyaltySettings.vue'

  const DEFAULT_SENDER_OPTIONS = [
    { value: 'sengrid', text: 'sengrid' },
    { value: 'customer', text: 'customer' },
    { value: 'SMTP', text: 'SMTP' },
  ]

  export default {
    name: 'UpdateForm',
    components: {
      FieldRenderer,
      LoyaltySettings,
    },
    props: {
      value: { default: false },
      refresh: { default: false },
      item: { required: true, type: Object },
      initItem: { required: true, type: Object },
      gameTypes: { default: [], type: Array },
      mailItems: { type: Array, default: () => [] },
    },
    data: () => ({
      loading: false,
      template: null,
      templates: [],
      isLoyaltyValid: true,
      holdTimeList: [
        { value: '00:00', text: '0h' },
        { value: '01:00', text: '1h' },
        { value: '02:00', text: '2h' },
        { value: '03:00', text: '3h' },
        { value: '04:00', text: '4h' },
        { value: '05:00', text: '5h' },
        { value: '06:00', text: '6h' },
        { value: '07:00', text: '7h' },
        { value: '08:00', text: '8h' },
        { value: '09:00', text: '9h' },
        { value: '10:00', text: '10h' },
        { value: '11:00', text: '11h' },
        { value: '12:00', text: '12h' },
        { value: '13:00', text: '13h' },
        { value: '14:00', text: '14h' },
        { value: '15:00', text: '15h' },
        { value: '16:00', text: '16h' },
        { value: '17:00', text: '17h' },
        { value: '18:00', text: '18h' },
        { value: '19:00', text: '19h' },
        { value: '20:00', text: '20h' },
        { value: '21:00', text: '21h' },
        { value: '22:00', text: '22h' },
        { value: '23:00', text: '23h' },
        { value: '24:00', text: '24h' },
      ],
      editableFields: [
        { label: 'Withdraw Hold Time', key: 'payment_handler.withdraw.hold_time', type: 'select' },
        { label: 'Licence', key: 'licence' },
        { label: 'GA Blacklist', key: 'ga_blacklist', type: 'boolean' },
        { label: 'Only Primary', key: 'only_primary', type: 'boolean' },
        // { label: 'Wager Multiplier', key: 'wager_multiplier', type: 'number' },
        { label: 'Withdraw Pending Limit', key: 'withdraw_pending_limit', type: 'number' },
        { label: 'Primary Verification Required', key: 'primary_verification_required', type: 'boolean' },
        { label: 'Primary Verification Exclude Countries', key: 'primary_verification_exclusion', type: 'country' },
        { label: 'Registration Closed', key: 'registration_closed', type: 'boolean' },
        { label: 'Daily Cashout Limit', key: 'daily_cashout_limit', type: 'number' },
        { label: 'Deposit Rollover', key: 'deposit_rollover', type: 'number' },
        { label: 'Bonuses Active Allowed', key: 'bonuses.active_allowed', type: 'number' },
        { label: 'Bonuses Cancel Disabled', key: 'bonuses.cancel_disabled', type: 'boolean' },
        { label: 'Bonuses Min Balance', key: 'bonuses.min_balance', type: 'number' },
        { label: 'Bonuses Withdraw Disabled', key: 'bonuses.withdraw_disabled', type: 'boolean' },
        { label: 'Bonuses Cancel Threshold', key: 'bonuses.cancel_threshold', type: 'boolean' },
        { label: 'Bonuses Bet Limits Sports Min', key: 'bonuses.bet_limits.sports.min', type: 'number' },
        { label: 'Bonuses Bet Limits Sports Max', key: 'bonuses.bet_limits.sports.max', type: 'number' },
        { label: 'Bonuses Bet Limits Default Min', key: 'bonuses.bet_limits.default.min', type: 'number' },
        { label: 'Bonuses Bet Limits Default Max', key: 'bonuses.bet_limits.default.max', type: 'number' },
        { label: 'Disable New Deposit Verification', key: 'disable_new_deposit_verification', type: 'boolean' },
        { label: 'Chat Config', key: 'chat_config', type: 'json' },
        { label: 'Freeze Accounts On Login Attempts Reached', key: 'is_login_freeze_enabled', type: 'boolean' },
        { label: 'Login Attempts', key: 'max_login_attempts', type: 'number' },
        { label: 'Login Failed Timeout (Minutes)', key: 'login_failed_timeout', type: 'number' },
        { label: 'Hide Paymethod Names', key: 'hide_paymethod_names', type: 'boolean' },
        { label: 'Skip Autopublish Status', key: 'skip_autopublish_status', type: 'boolean' },
        { label: 'Skip Autoload Images', key: 'skip_autoload_images', type: 'boolean' },
        { label: 'Email Verification TTL (Minutes)', key: 'email_options.verification_ttl_minutes', type: 'number' },
        { label: 'Session TTL (Seconds)', key: 'session.ttl', type: 'duration' },
        { label: 'Disable Bonus Fast Withdraw', key: 'disable_bonus_fast_withdraw', type: 'autocomplete', choices: [ 'cashback', 'achievement', 'journey', 'autobonus', 'promocode', 'event', 'wheel', 'empty', 'luckybox', 'respin' ] },
        { label: 'Pending Withdrawal Auto-Close Period (Days)', key: 'cancel_unverified_withdraw_days', type: 'number', },
        { label: 'Fast Withdraw Enabled for VIP', key: 'fast_withdraw_settings.enabled_for_vip', type: 'boolean' },
        { label: 'Fast Withdraw Enabled for Non-VIP', key: 'fast_withdraw_settings.enabled_for_no_vip', type: 'boolean' },
        { label: 'Non-VIP Release Threshold', key: 'fast_withdraw_settings.no_vip_release_threshold', type: 'number' },
        { label: 'Events Min Win', key: 'events.info_events.minWin', type: 'number' },
        { label: 'Events Test Activity', key: 'events.info_events.testActivity', type: 'boolean' },
        { label: 'Events Login Events', key: 'events.info_events.loginEvents', type: 'boolean' },
        { label: 'Winstats Min Win', key: 'events.info_winstats.minWin', type: 'number' },
        { label: 'Wins Min Win', key: 'events.info_wins.minWin', type: 'number' },
        { label: 'Wins Min Big Win', key: 'events.info_wins.minBigWin', type: 'number' },
        { label: 'Withdrawal Status Messages', key: 'withdrawal_status_messages', type: 'boolean' },
        { label: 'Magic Link Login', key: 'email_options.magiclink_passwordless', type: 'boolean' },
        {
          label: 'Sport Rollover',
          key: 'sport_rollover',
          type: 'group',
          fields: [
            { label: 'Singlebet', key: 'bonuses.sport_rollover.singlebet', type: 'number' },
            { label: 'Virtual Sport (Name)', key: 'bonuses.sport_rollover.virtual_sport.name', type: 'combobox' },
            { label: 'Virtual Sport (Category)', key: 'bonuses.sport_rollover.virtual_sport.category', type: 'combobox' },
            { label: 'Market Name', key: 'bonuses.sport_rollover.market_name', type: 'combobox' },
            { label: 'Outcome Name', key: 'bonuses.sport_rollover.outcome_name', type: 'autocomplete', choices: ['over', 'under'] },
            { label: 'Multibet Count', key: 'bonuses.sport_rollover.multibet.count', type: 'number' },
            { label: 'Odds', key: 'bonuses.sport_rollover.odds', type: 'number' },
            { label: 'Max Rollover', key: 'bonuses.sport_rollover.max_rollover', type: 'number' },
          ]
        },
        {
          label: 'Loyalty',
          key: 'loyalty',
          type: 'group',
          fields: [
            { label: 'Limits', key: 'loyalty.limit', type: 'number' },
            { label: 'Loyalty Default', key: 'loyalty.gametype.default', type: 'number' },
          ]
        },
        {
          label: 'Express Withdrawal',
          key: 'express_withdrawal',
          type: 'group',
          fields: [
            { label: 'Is Enabled', key: 'express_withdrawal.is_enabled', type: 'boolean' },
            { label: 'Fee Percentage (1-100)', key: 'express_withdrawal.fee_percentage', type: 'number' },
          ],
        },
        {
          label: 'Mail',
          key: 'mail_settings',
          type: 'group',
          fields: [
            { label: 'Default sender', key: 'default_sender', type: 'select' },
            { label: 'Birthday', key: 'birthday', type: 'select' },
            { label: 'Notification', key: 'notification', type: 'select' },
            { label: 'Deposit Error', key: 'deposit_error', type: 'select' },
            { label: 'Retention Mail', key: 'retention_mail', type: 'select' },
            { label: 'Withdraw Complete', key: 'withdraw_complete', type: 'select' },
            { label: 'Bonus Notification', key: 'bonus_notification', type: 'select' },
          ],
        },

      ],
    }),

    computed: {
      mailSelectOptions() {
        return (this.mailItems ?? []).map((m) => ({
          value: m.mail_settings_id,
          text: m.settings?.email ?? String(m.mail_settings_id),
        }))
      },
    },

    methods: {
      keys (obj) {
        if (!obj) return {}
        return Object.keys(obj)
      },

      handleLoyaltyValidationChange(isValid) {
        this.isLoyaltyValid = isValid
      },

      getMailFieldWithChoices(subField) {
        if (subField.key === 'default_sender') {
          return { ...subField, choices: DEFAULT_SENDER_OPTIONS }
        }
        return { ...subField, choices: this.mailSelectOptions }
      },

      async save (item, message = 'Save entity setting', saveAndClose = true) {
        let buttons = []
        const uniqItem = removeDuplicateValues(item, this.initItem)
        if (!saveAndClose) {
          buttons = [
            { text: 'Save', value: 'close', class: 'primary darken-1' },
            { text: 'Cancel', value: false, class: 'grey' },
          ]
        } else {
          buttons = [
            { text: 'Save', value: true, class: 'primary darken-1' },
            { text: 'Save and close', value: 'close', class: 'primary darken-1' },
            { text: 'Cancel', value: false, class: 'grey' },
          ]
        }
        if (!Object.keys(uniqItem).length) {
          await this.$root.$confirm('No changes found', 'Please, update the entity settings', {
            width: 515,
            buttons: [ { text: 'Ok', value: false, class: 'primary darken-1' } ]
          })
          return
        }

        let confirmRes = await this.$root.$confirm('Confirm', message, { width: 515, buttons })

        if (!confirmRes) {
          return
        }

        this.loading = true

        return API.call('/settings/entity/update.json', {
          entity_id: this.item.entity_id,
          settings: uniqItem
        }).then(resp => {

          this.$showSuccess('Entity settings updated')

          return this.refresh()
        }).finally(() => {
          this.loading = false

          if (confirmRes == 'close') {
            this.$emit('input')
          }
        })
      },
    }
  }
</script>

<style scoped>
</style>
